/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Checkbox,
  Drawer,
  FormHelperText,
  IconButton,
  InputAdornment,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LoadingSpinner } from "../../../components/loading_spinner";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import {
  StyledAddButton,
  StyledButtonContainer,
  StyledCancelButton,
  StyledCreateTitle,
  StyledFormContainer,
  StyledFormFieldText,
  StyledTextField,
} from "../../restaurant_groups/styles";
import { StyledDatePicker, StyledSelect } from "../../network_guests/styles";
import { StyledFormControlLabel, StyledIconButton } from "../styles";

const getFieldError = (fieldError) => {
  if (Array.isArray(fieldError)) {
    return fieldError[0]
  }

  return fieldError || ''
}

const buildErrorHelperTextSx = (theme, extraSx = {}) => ({
  color: theme.palette.error.main,
  marginLeft: '14px',
  ...extraSx,
})

const TimeSlotFields = ({
  timeSlots = [],
  hasAllDayTimeSlot,
  timeOptions = [],
  timeSlotsError,
  onAllDayToggle,
  onTimeSlotChange,
  onAddTimeSlot,
  onDeleteTimeSlot,
}) => {
  const theme = useTheme()

  return (
    <div style={{ marginTop: '3%' }}>
      {timeSlots.map((timeSlot, index) => (
        <Box
          key={`time-slot-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr) minmax(0, 1fr) 40px',
            columnGap: 1,
            alignItems: 'end',
            marginTop: index === 0 ? 0 : '14px',
          }}
        >
          <StyledFormControlLabel
            control={(
              <Checkbox
                checked={!!timeSlot?.all_day}
                onChange={(event) => onAllDayToggle(index, event.target.checked)}
                size="small"
                sx={{
                  color: theme.palette.grey[300],
                  '&.Mui-checked': {
                    color: theme.palette.warning.main,
                  },
                }}
              />
            )}
            label="All day"
          />

          <Box sx={{ minWidth: 0 }}>
            <StyledFormFieldText>Start time</StyledFormFieldText>
            <StyledSelect
              fullWidth
              displayEmpty
              value={timeSlot?.start_time}
              onChange={(event) => onTimeSlotChange(index, 'start_time', event.target.value)}
              disabled={!!timeSlot?.all_day}
              renderValue={(selected) => selected || 'Select time'}
              error={!timeSlot?.start_time && Boolean(timeSlotsError)}
              data-testid={`event-start-time-input-${index}`}
              MenuProps={{
                style: {
                  maxHeight: 400,
                },
              }}
            >
              {timeOptions.map((time) => (
                <StyledMenuItem key={`start-time-${time}`} value={time}>
                  {time}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <StyledFormFieldText>End time</StyledFormFieldText>
            <StyledSelect
              fullWidth
              displayEmpty
              value={timeSlot?.end_time}
              onChange={(event) => onTimeSlotChange(index, 'end_time', event.target.value)}
              disabled={!!timeSlot?.all_day}
              renderValue={(selected) => selected || 'Select time'}
              error={!timeSlot?.end_time && Boolean(timeSlotsError)}
              data-testid={`event-end-time-input-${index}`}
              MenuProps={{
                style: {
                  maxHeight: 400,
                },
              }}
            >
              {timeOptions.map((time) => (
                <StyledMenuItem key={`end-time-${time}`} value={time}>
                  {time}
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </Box>

          <StyledIconButton
            onClick={index === 0 ? onAddTimeSlot : () => onDeleteTimeSlot(index)}
            disabled={index === 0 && hasAllDayTimeSlot}
            data-testid={index === 0 ? 'add-time-slot-button' : `delete-time-slot-button-${index}`}
          >
            {index === 0 ? <AddIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
          </StyledIconButton>
        </Box>
      ))}

      {!!timeSlotsError && (
        <FormHelperText sx={buildErrorHelperTextSx(theme, { marginTop: '8px' })}>
          {timeSlotsError}
        </FormHelperText>
      )}
    </div>
  )
}

export const EventFormDrawer = ({
  open,
  mode,
  title,
  drawerWidth,
  eventData,
  errors,
  loading,
  timeOptions,
  hasAllDayTimeSlot,
  createRestaurantGroupsList,
  setCreateRestaurantGroupSearch,
  getCreateRestaurantGroupName,
  createRestaurantGroupMenuRef,
  createRestaurantGroupLoaderRef,
  createRestaurantGroupsHasNextPage,
  createRestaurantGroupsFetchingNextPage,
  createRestaurantsList,
  createRestaurantSearch,
  setCreateRestaurantSearch,
  getCreateRestaurantName,
  createRestaurantMenuRef,
  createRestaurantLoaderRef,
  createRestaurantsHasNextPage,
  createRestaurantsFetchingNextPage,
  dynamicDiscountModeOptions = [],
  onFieldChange,
  onDateChange,
  onAllDayToggle,
  onTimeSlotChange,
  onAddTimeSlot,
  onDeleteTimeSlot,
  onClose,
  onSubmit,
  createRestaurantGroupFetching,
  createRestaurantFetching,
}) => {
  const theme = useTheme()
  const isCreateMode = mode === 'create'
  const restaurantPlaceholder = 'Select restaurant'
  const requiredMark = <Box component="span" sx={{ color: theme.palette.error.main }}>*</Box>
  const fieldErrorSx = buildErrorHelperTextSx(theme)
  const nameError = getFieldError(errors?.name)
  const restaurantGroupError = getFieldError(errors?.restaurant_group_id)
  const restaurantError = getFieldError(errors?.restaurant_id)
  const dynamicDiscountEventModeError = getFieldError(errors?.dynamic_discount_event_mode)
  const dynamicDiscountCardModeError = getFieldError(errors?.dynamic_discount_card_mode)
  const startDateError = getFieldError(errors?.start_date)
  const endDateError = getFieldError(errors?.end_date)
  const timeSlotsError = getFieldError(errors?.time_slots)
  const submitLabel = isCreateMode ? 'Create' : 'Update'
  const submitTestId = isCreateMode ? 'event-create-submit-button' : 'event-edit-submit-button'
  const cancelTestId = isCreateMode ? 'event-create-cancel-button' : 'event-edit-cancel-button'
  const drawerTestId = isCreateMode ? 'event-create-drawer' : 'event-edit-drawer'

  const preventRestaurantClearMouseDown = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleRestaurantClear = (event) => {
    preventRestaurantClearMouseDown(event)
    onFieldChange('', 'restaurant_id')
  }

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          marginTop: 'calc(35px * 2)',
          height: 'calc(100% - 70px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      variant="persistent"
      anchor="right"
      open={open}
      data-testid={drawerTestId}
    >
      <Box
        data-testid="event-form-drawer-scroll-container"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          pt: 1,
          pb: 4,
        }}
      >
        <StyledCreateTitle>{title}</StyledCreateTitle>

        <StyledFormContainer style={{ marginTop: '24px', paddingBottom: '32px' }}>
          {isCreateMode ? (
            <div style={{ marginTop: '3%' }}>
              <StyledFormFieldText>Restaurant group{requiredMark}</StyledFormFieldText>
              <Box>
                <StyledSelect
                  value={eventData?.restaurant_group_id}
                  onChange={(event) => onFieldChange(event.target.value, 'restaurant_group_id')}
                  onClose={() => setTimeout(() => setCreateRestaurantGroupSearch(''), 1000)}
                  MenuProps={{
                    PaperProps: {
                      ref: createRestaurantGroupMenuRef,
                      sx: {
                        maxHeight: 400,
                      },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  renderValue={() => getCreateRestaurantGroupName(eventData?.restaurant_group_id)}
                  error={Boolean(restaurantGroupError)}
                  data-testid="restaurant-group-select"
                >
                  <DropdownSearchBar changeSearchText={setCreateRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={createRestaurantGroupFetching} />
                  {createRestaurantGroupsList?.map((restaurantGroup) => (
                    <StyledMenuItem
                      key={restaurantGroup?.value}
                      value={restaurantGroup?.value}
                    >
                      {eventData?.restaurant_group_id === restaurantGroup?.value
                        ? <SelectedMenuItem data={restaurantGroup?.label} />
                        : restaurantGroup?.label}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={createRestaurantGroupsHasNextPage}
                    targetRef={createRestaurantGroupLoaderRef}
                  />
                </StyledSelect>
                {!!restaurantGroupError && (
                  <FormHelperText sx={fieldErrorSx}>
                    {restaurantGroupError}
                  </FormHelperText>
                )}
              </Box>
            </div>
          ) : (
            <div style={{ marginTop: '3%' }}>
              <StyledFormFieldText>Restaurant Group</StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                disabled
                value={eventData?.restaurant_group?.name}
                inputProps={{
                  'data-testid': 'event-restaurant-group-input'
                }}
              />
            </div>
          )}

          <div style={{ marginTop: '3%' }}>
            <StyledFormFieldText>Restaurant (Optional)</StyledFormFieldText>
            <Box>
              <StyledSelect
                value={eventData?.restaurant_id || ''}
                onChange={(event) => onFieldChange(event.target.value, 'restaurant_id')}
                onClose={() => setTimeout(() => setCreateRestaurantSearch(''), 1000)}
                MenuProps={{
                  PaperProps: {
                    ref: createRestaurantMenuRef,
                    sx: {
                      maxHeight: 400,
                    },
                  },
                  MenuListProps: { sx: { pt: 0 } },
                }}
                disabled={!eventData?.restaurant_group_id}
                displayEmpty
                renderValue={(selected) => getCreateRestaurantName(selected) || restaurantPlaceholder}
                error={Boolean(restaurantError)}
                endAdornment={eventData?.restaurant_id ? (
                  <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                    <IconButton
                      edge="end"
                      size="small"
                      disableRipple
                      data-testid="restaurant-select-clear-button"
                      onMouseDown={preventRestaurantClearMouseDown}
                      onClick={handleRestaurantClear}
                    >
                      <ClearIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null}
                data-testid="restaurant-select"
              >
                <DropdownSearchBar changeSearchText={setCreateRestaurantSearch} placeholder={'Search restaurant'} isFetching={createRestaurantFetching} />
                {createRestaurantsList?.map((restaurant) => (
                  <StyledMenuItem
                    key={restaurant?.value}
                    value={restaurant?.value}
                  >
                    {eventData?.restaurant_id === restaurant?.value
                      ? <SelectedMenuItem data={restaurant?.label} />
                      : restaurant?.label}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={createRestaurantsHasNextPage}
                  targetRef={createRestaurantLoaderRef}
                />
              </StyledSelect>
              {!!restaurantError && (
                <FormHelperText sx={fieldErrorSx}>
                  {restaurantError}
                </FormHelperText>
              )}
            </Box>
          </div>

          {eventData?.restaurant_id && (
            <React.Fragment>
              <div style={{ marginTop: '3%' }} data-testid="dynamic-discount-event-mode-group">
                <StyledFormFieldText>Event mode{requiredMark}</StyledFormFieldText>
                <RadioGroup
                  row
                  value={eventData?.dynamic_discount_event_mode || ''}
                  onChange={(event) => onFieldChange(event.target.value, 'dynamic_discount_event_mode')}
                >
                  {dynamicDiscountModeOptions.map((option) => (
                    <FormControlLabel
                      key={`event-mode-${option.value}`}
                      value={option.value}
                      control={(
                        <Radio
                          size="small"
                          inputProps={{
                            'data-testid': `dynamic-discount-event-mode-${option.value}`
                          }}
                        />
                      )}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {!!dynamicDiscountEventModeError && (
                  <FormHelperText sx={fieldErrorSx}>
                    {dynamicDiscountEventModeError}
                  </FormHelperText>
                )}
              </div>

              <div style={{ marginTop: '3%' }} data-testid="dynamic-discount-card-mode-group">
                <StyledFormFieldText>Card mode{requiredMark}</StyledFormFieldText>
                <RadioGroup
                  row
                  value={eventData?.dynamic_discount_card_mode || ''}
                  onChange={(event) => onFieldChange(event.target.value, 'dynamic_discount_card_mode')}
                >
                  {dynamicDiscountModeOptions.map((option) => (
                    <FormControlLabel
                      key={`card-mode-${option.value}`}
                      value={option.value}
                      control={(
                        <Radio
                          size="small"
                          inputProps={{
                            'data-testid': `dynamic-discount-card-mode-${option.value}`
                          }}
                        />
                      )}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {!!dynamicDiscountCardModeError && (
                  <FormHelperText sx={fieldErrorSx}>
                    {dynamicDiscountCardModeError}
                  </FormHelperText>
                )}
              </div>
            </React.Fragment>
          )}

          <div style={{ marginTop: '3%' }}>
            <StyledFormFieldText>Event name{requiredMark}</StyledFormFieldText>
            <StyledTextField
              fullWidth
              size="small"
              onChange={(event) => onFieldChange(event.target.value, 'name')}
              error={Boolean(nameError)}
              helperText={nameError}
              value={eventData?.name}
              inputProps={{
                'data-testid': 'event-name-input'
              }}
            />
          </div>

          <div style={{ marginTop: '3%' }}>
            <StyledFormFieldText>Start date{isCreateMode ? requiredMark : null}</StyledFormFieldText>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                value={eventData?.start_date ? dayjs(eventData.start_date) : null}
                onChange={(event) => onDateChange(event, 'start_date')}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    inputProps: {
                      'data-testid': 'event-start-date-input'
                    },
                    error: Boolean(startDateError),
                    helperText: startDateError,
                  },
                }}
              />
            </LocalizationProvider>
          </div>

          <div style={{ marginTop: '3%' }}>
            <StyledFormFieldText>End date</StyledFormFieldText>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                value={eventData?.end_date ? dayjs(eventData.end_date) : null}
                onChange={(event) => onDateChange(event, 'end_date')}
                minDate={isCreateMode ? dayjs() : undefined}
                slotProps={{
                  textField: {
                    inputProps: {
                      'data-testid': 'event-end-date-input'
                    },
                    error: Boolean(endDateError),
                    helperText: endDateError,
                  },
                }}
              />
            </LocalizationProvider>
          </div>

          <TimeSlotFields
            timeSlots={eventData?.time_slots || []}
            hasAllDayTimeSlot={hasAllDayTimeSlot}
            timeOptions={timeOptions}
            timeSlotsError={timeSlotsError}
            onAllDayToggle={onAllDayToggle}
            onTimeSlotChange={onTimeSlotChange}
            onAddTimeSlot={onAddTimeSlot}
            onDeleteTimeSlot={onDeleteTimeSlot}
          />
        </StyledFormContainer>
      </Box>

      <Paper
        data-testid="event-form-drawer-actions"
        square
        sx={{
          flexShrink: 0,
          width: '100%',
          height: '70px'
        }}
        elevation={2}
      >
        <StyledButtonContainer>
          <StyledCancelButton
            onClick={onClose}
            variant="outlined"
            data-testid={cancelTestId}
          >
            Cancel
          </StyledCancelButton>
          <StyledAddButton
            onClick={onSubmit}
            variant="contained"
            disabled={loading}
            data-testid={submitTestId}
          >
            {loading ? <LoadingSpinner isLoading /> : submitLabel}
          </StyledAddButton>
        </StyledButtonContainer>
      </Paper>
    </Drawer>
  )
}
